import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { STATUS_ROLES } from './constants';
import { optionClasses } from './AutosuggestOption';

function AutosuggestOptions({
  getItemProps,
  highlightedIndex,
  selectedItem,
  options,
  isShown,
  isLoading,
  inputValue,
  loadingMessage,
  itemToString,
  getMenuProps,
  AutosuggestOptionComponent,
}) {
  const { optionItems, statusItems } = useMemo(() => {
    if (isLoading) {
      return {
        optionItems: [],
        statusItems: [
          { id: 'loading', disabled: true, toDisplay: loadingMessage },
        ],
      };
    }
    const status = [];
    const opts = [];
    // Items are split into two buckets:
    //   - status: non-interactive rows announced via aria-hidden status text
    //     (anything with a statusRole, OR any disabled row that isn't otherwise
    //     a normal option). Disabled-but-no-statusRole rows are treated as
    //     informational messages, not as selectable-but-disabled options.
    //   - opts: normal selectable options (rendered in the listbox)
    (options || []).forEach((item, index) => {
      if (STATUS_ROLES.includes(item?.statusRole) || item?.disabled) {
        status.push(item);
      } else {
        opts.push({ item, index });
      }
    });
    return { optionItems: opts, statusItems: status };
  }, [options, isLoading, loadingMessage]);

  const hasAny = optionItems.length > 0 || statusItems.length > 0;

  return (
    <div
      className="dropdown"
      style={{ display: !isShown || !hasAny ? 'none' : 'block' }}
      data-testid="autosuggest-options"
    >
      {statusItems.length > 0 && (
        <div className="dropdown-status" aria-hidden="true">
          {statusItems.map((item, i) => (
            <div
              key={item.id || `${itemToString(item)}-${i}`}
              className={classNames(
                optionClasses(false),
                'dropdown-status-item',
              )}
              data-testid={`autosuggest-option-${
                item.id || `${itemToString(item)}-${i}`
              }`}
            >
              {itemToString(item)}
            </div>
          ))}
        </div>
      )}
      <ul className="dropdown-listbox" {...getMenuProps()}>
        {optionItems.map(({ item, index }) => (
          // `index` is the position in the full `options` array (not the filtered list),
          // which is what downshift uses internally for getItemProps.
          <AutosuggestOptionComponent
            key={item.id || `${item}-${index}`}
            item={item}
            index={index}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedItem={selectedItem}
            itemToString={itemToString}
            inputValue={inputValue}
          />
        ))}
      </ul>
    </div>
  );
}

AutosuggestOptions.propTypes = {
  getItemProps: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
  isShown: PropTypes.bool.isRequired,
  itemToString: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  AutosuggestOptionComponent: PropTypes.elementType,
  highlightedIndex: PropTypes.number,
  inputValue: PropTypes.string,
  isLoading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  selectedItem: PropTypes.object,
};

export default AutosuggestOptions;
